import { useQueryClient } from '@tanstack/react-query';
import { logout } from 'app/lib/analytics';
import { StorageKey, storage } from 'app/lib/storage';
import { api401DetectedAtom, isLoggingOutAtom, useResetAtoms } from 'app/atoms';
import { useAtomValue, useSetAtom } from 'jotai';
import { setBadgeCount } from 'app/lib/badge';
import { useEffect } from 'react';
import { clearLastExecution } from 'app/lib/query';
import { clearSessionState } from 'app/lib/axios/session-state';
import { clearPersistedQueryCache } from 'app/lib/query-persistence';
import { recordError } from 'app/lib/monitoring';

const analyticsLogout = () => logout();

async function performLogout(resetAtoms: () => void, clearQueryClient: () => void, finishLoggingOut: () => void) {
  try {
    analyticsLogout();
    await storage.multiClear([StorageKey.AccessToken, StorageKey.RefreshToken]);
    void storage.multiClear(
      Object.values(StorageKey).filter(
        (key) =>
          key !== StorageKey.NewlyInsuredModalShown &&
          key !== StorageKey.GeneratedDeviceUUID &&
          key !== StorageKey.PreferredApiBaseUrl &&
          key !== StorageKey.RegionMigrationAttempt &&
          key !== StorageKey.LastAuthenticationMethod,
      ),
    );
    setBadgeCount(0, 'logout');
    resetAtoms();
    clearLastExecution();
    clearSessionState();
    clearQueryClient();
    try {
      await clearPersistedQueryCache();
    } catch (error) {
      recordError(error);
    }
  } finally {
    finishLoggingOut();
  }
}

export function useLogoutRoutine() {
  const queryClient = useQueryClient();
  const resetAtoms = useResetAtoms();
  const setIsLoggingOut = useSetAtom(isLoggingOutAtom);

  return () =>
    performLogout(
      resetAtoms,
      () => queryClient.clear(),
      () => setIsLoggingOut(false),
    );
}

/**
 * Watches for 401 Unauthorized API errors (signalled via api401DetectedAtom by useQueryLPC)
 * and triggers the logout routine when detected.
 * Must be called once at the app root level (inside providers).
 */
export function useApi401Logout() {
  const api401Detected = useAtomValue(api401DetectedAtom);
  const setApi401Detected = useSetAtom(api401DetectedAtom);
  const isLoggingOut = useAtomValue(isLoggingOutAtom);
  const setIsLoggingOut = useSetAtom(isLoggingOutAtom);
  const logoutRoutine = useLogoutRoutine();

  useEffect(() => {
    if (api401Detected && !isLoggingOut) {
      setApi401Detected(false);
      setIsLoggingOut(true);
      void logoutRoutine();
    }
  }, [api401Detected, isLoggingOut, logoutRoutine, setApi401Detected, setIsLoggingOut]);
}
